import { t } from "@lingui/macro";
import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import { ErrorBoundary } from "@sentry/react-native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { isNil } from "lodash";
import { Suspense, useCallback, useEffect } from "react";
import {
  PreloadedQuery,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";

import { normalizeSliderTimeValue } from "@/components/AgendaFilters/utils/getSliderOptions";
import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import {
  courseAvailabilityQueryInput_groupSize,
  courseAvailabilityQueryInput_holeCount,
  courseAvailabilityQueryInput_timeRange,
} from "@/screens/DepartmentAvailability/atoms/courseAvailabilityQueryInputs";
import { setLastSelectedBookingSelection } from "@/utils/bookingSelectionStorage";
import { Button } from "@whoosh/design-system/src/components/controls";
import { SkeletonLoader } from "@whoosh/design-system/src/components/display";
import { ArrowRightIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { CourseCardSummaryQuery } from "./__generated__/CourseCardSummaryQuery.graphql";
import { CourseCardSummary_course$key } from "./__generated__/CourseCardSummary_course.graphql";

type Props = {
  courseRef: CourseCardSummary_course$key | null;
  onNavigate?: (navigate: () => void) => void;
};
export const CourseCardSummary = ({ courseRef, onNavigate }: Props) => {
  const timeRange = useAtomValue(courseAvailabilityQueryInput_timeRange);
  const groupSize = useAtomValue(courseAvailabilityQueryInput_groupSize);
  const holeCount = useAtomValue(courseAvailabilityQueryInput_holeCount);
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();
  const date = route?.params?.date;

  const course = useFragment(CourseCardSummaryFragmentString, courseRef);
  const courseId = course?.id;
  const facilityId = course?.facility?.id;
  const facilitySlug = course?.facility?.slug;

  const navigation =
    useNavigation<NavigationProp<ClubStackNavigatorParamList>>();
  const normalizedTimeGte = normalizeSliderTimeValue(timeRange?.timeGTE);
  const normalizedTimeLte = normalizeSliderTimeValue(timeRange?.timeLTE);

  const navigateToTeeSheet = useCallback(() => {
    if (!facilitySlug || !courseId || !date || !facilityId) return;
    setLastSelectedBookingSelection({ facilityId, courseId });
    navigation.navigate("Agenda", {
      facilitySlug,
      courseId,
      date,
      timeGTE: normalizedTimeGte,
      timeLTE: normalizedTimeLte,
      holeCount,
      groupSize,
    });
  }, [
    courseId,
    date,
    facilityId,
    facilitySlug,
    groupSize,
    holeCount,
    navigation,
    normalizedTimeGte,
    normalizedTimeLte,
  ]);

  useEffect(() => {
    if (onNavigate) onNavigate(navigateToTeeSheet);
  }, [onNavigate, navigateToTeeSheet]);

  const [queryRef, loadQuery] = useQueryLoader<CourseCardSummaryQuery>(
    CourseCardSummaryQueryString
  );

  useEffect(() => {
    if (courseId && facilityId && date) {
      loadQuery(
        {
          facilityId,
          courseId,
          date,
          groupSize,
          holeCount,
          timeGte: normalizedTimeGte,
          timeLte: normalizedTimeLte,
        },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [
    courseId,
    date,
    facilityId,
    groupSize,
    holeCount,
    loadQuery,
    normalizedTimeGte,
    normalizedTimeLte,
  ]);

  return courseId && date && facilitySlug ? (
    <ErrorBoundary fallback={<SummaryError />}>
      <Suspense fallback={<SummarySkeleton />}>
        {queryRef ? (
          <CourseCardSummaryInner
            queryRef={queryRef}
            courseId={courseId}
            date={date}
            facilitySlug={facilitySlug}
            navigateToTeeSheet={navigateToTeeSheet}
          />
        ) : null}
      </Suspense>
    </ErrorBoundary>
  ) : null;
};

type InnerProps = {
  queryRef: PreloadedQuery<CourseCardSummaryQuery>;
  courseId: string;
  date: string;
  facilitySlug: string;
  navigateToTeeSheet: () => void;
};
const CourseCardSummaryInner = ({
  queryRef,
  navigateToTeeSheet,
}: InnerProps) => {
  const { currencySymbol } = useGetClubCurrency();
  const data = usePreloadedQuery(CourseCardSummaryQueryString, queryRef);

  const firstTime =
    data?.facility?.agendas?.edges?.[0]?.node?.agendaSummary?.firstTime;
  const lastTime =
    data?.facility?.agendas?.edges?.[0]?.node?.agendaSummary?.lastTime;

  const hasAvailability = firstTime && lastTime;

  const timeString = hasAvailability
    ? `${formatHHMM(firstTime)} - ${formatHHMM(lastTime)}`
    : undefined;

  const minPrice =
    data?.facility?.agendas?.edges?.[0]?.node?.agendaSummary?.minPrice;
  const maxPrice =
    data?.facility?.agendas?.edges?.[0]?.node?.agendaSummary?.maxPrice;

  const priceRangeString =
    !isNil(minPrice) && !isNil(maxPrice)
      ? minPrice === maxPrice
        ? `${currencySymbol}${ConvertCentsToDollars(minPrice)}`
        : `${currencySymbol}${ConvertCentsToDollars(
            minPrice
          )} - ${currencySymbol}${ConvertCentsToDollars(maxPrice)}`
      : undefined;

  return (
    <HStack justifyContent="space-between" alignItems="center" gap={4}>
      {hasAvailability ? (
        <HStack justifyContent="space-between" flex={1} gap={2}>
          {priceRangeString ? (
            <VStack width="50%" gap={".5"}>
              <Text
                variant="mainBaseSemiBold"
                color="text-secondary"
              >{t`Price`}</Text>
              <Text variant={{ base: "mainSmallSemiBold", sm: "mainHeading" }}>
                {priceRangeString}
              </Text>
            </VStack>
          ) : null}
          <VStack width="50%" gap={".5"}>
            <Text
              variant="mainBaseSemiBold"
              color="text-secondary"
            >{t`Availability`}</Text>
            <Text variant={{ base: "mainSmallSemiBold", sm: "mainHeading" }}>
              {timeString}
            </Text>
          </VStack>
        </HStack>
      ) : (
        <Box
          p={{ base: 2, sm: 3 }}
          borderColor="outline-default"
          borderWidth={1}
          borderRadius="2"
        >
          <Text
            variant="mainBaseSemiBold"
            color="text-secondary"
          >{t`No Availability`}</Text>
        </Box>
      )}
      <Button
        accessibilityLabel={t`Navigate to Tee Sheet`}
        size="small"
        variant={hasAvailability ? "main" : "secondary"}
        leftIcon={<ArrowRightIcon />}
        onPress={navigateToTeeSheet}
      />
    </HStack>
  );
};

const SummaryError = () => {
  return (
    <HStack justifyContent="space-between" alignItems="center" gap={4}>
      <Box
        p={{ base: 2, sm: 3 }}
        borderColor="outline-status-error"
        borderWidth={1}
        borderRadius="2"
      >
        <Text
          variant="mainBaseSemiBold"
          color="text-error"
        >{t`Could Not Retrieve Availability`}</Text>
      </Box>
      <Button
        accessibilityLabel={t`Navigate to Tee Sheet`}
        size="small"
        variant="secondary"
        leftIcon={<ArrowRightIcon />}
        isDisabled
      />
    </HStack>
  );
};

const SummarySkeleton = () => {
  return (
    <HStack justifyContent="space-between" alignItems="center" gap={4}>
      <HStack justifyContent="space-between" flex={1} gap={2}>
        <VStack width="50%" gap={".5"}>
          <Text
            variant="mainBaseSemiBold"
            color="text-secondary"
          >{t`Price`}</Text>
          <SkeletonLoader
            height={{ base: 16, sm: 24 }}
            width={150}
            borderRadius="2"
          />
        </VStack>
        <VStack width="50%" gap={".5"}>
          <Text
            variant="mainBaseSemiBold"
            color="text-secondary"
          >{t`Availability`}</Text>
          <SkeletonLoader
            height={{ base: 16, sm: 24 }}
            width={150}
            borderRadius="2"
          />
        </VStack>
      </HStack>
      <SkeletonLoader height={48} width={48} borderRadius="2" />
    </HStack>
  );
};

const CourseCardSummaryFragmentString = graphql`
  fragment CourseCardSummary_course on Course {
    id
    facility {
      id
      slug
    }
  }
`;

const CourseCardSummaryQueryString = graphql`
  query CourseCardSummaryQuery(
    $facilityId: ID!
    $courseId: ID!
    $date: Date!
    $timeLte: Time
    $timeGte: Time
    $groupSize: Int
    $holeCount: HoleCount
  ) {
    facility: node(id: $facilityId) {
      ... on Facility {
        agendas(dateGte: $date, dateLte: $date) {
          edges {
            node {
              agendaSummary(
                courseIds: [$courseId]
                timeGte: $timeGte
                timeLte: $timeLte
                playerCount: $groupSize
                holeCount: $holeCount
              ) {
                firstTime
                lastTime
                minPrice
                maxPrice
              }
            }
          }
        }
      }
    }
  }
`;
